import { createSelector, createSlice, isAnyOf } from '@reduxjs/toolkit'

import { createDefectAction, options, postDigitalAssetAction } from '../actions/create-defect'
import {
  AssetBytes,
  AssetIdentifiers,
  CreateDefectPayload,
  CreateDefectResponse,
  dataWithStatus,
  OptionStates,
} from '../components/create-defect/types'
import type { AppState } from '../types'
import { ActionStatus } from '../types/Input'

type FormData = Omit<
  DefectState,
  | 'status'
  | 'error'
  | 'defectId'
  | 'response'
  | 'request'
  | 'preSelectedSerialNumbers'
  | 'attachmentUploadFailures'
>

export interface DefectState extends OptionStates {
  status: ActionStatus
  error?: string
  assetIdentifiers: dataWithStatus<AssetIdentifiers>
  assetBytes: AssetBytes
  request?: CreateDefectPayload
  response?: CreateDefectResponse
  attachmentUploadFailures: string[]
  preSelectedSerialNumbers: string[]
}

const initialState: Omit<DefectState, 'equipments'> = {
  status: 'none',
  defectGroups: { status: 'none', data: [] },
  items: { status: 'none', data: {} },
  defectTrees: { status: 'none', data: [] },
  positions: { status: 'none', data: [] },
  limitationsOfUsage: { status: 'none', data: [] },
  affectToUsage: { status: 'none', data: [] },
  assetIdentifiers: { status: 'none', data: {} },
  assetBytes: {},
  preSelectedSerialNumbers: [],
  attachmentUploadFailures: [],
}

export const createDefectSlice = createSlice({
  name: 'defect',
  initialState,
  reducers: {
    removeFile: (state, { payload }) => {
      delete state.assetIdentifiers.data[payload]
      delete state.assetBytes[payload]
    },
    clearFiles: (state) => {
      state.assetIdentifiers = initialState.assetIdentifiers
      state.assetBytes = initialState.assetBytes
    },
    clearDataByKey: (state, { payload }: { payload: keyof Omit<FormData, 'equipments'> }) => {
      state[payload].data = initialState[payload].data
    },
    resetStatus: (state) => {
      state.status = initialState.status
    },
    resetOptionsStatusByKey: (
      state,
      { payload }: { payload: keyof Omit<FormData, 'equipments'> }
    ) => {
      state[payload].status = initialState[payload].status
    },
    resetStoredResponseRequest: (state) => {
      state.response = initialState.response
      state.request = initialState.request
    },
    setPreSelectedSerialNumbers: (state, { payload }: { payload: string[] }) => {
      state.preSelectedSerialNumbers = payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createDefectAction.pending, (state) => {
      state.status = 'loading'
      state.error = 'undefined'
      state.attachmentUploadFailures = []
    })
    builder.addCase(createDefectAction.fulfilled, (state, { payload }) => {
      state.status = 'succeeded'
      state.response = payload.response
      state.request = payload.request
      state.attachmentUploadFailures = payload.attachmentUploadFailures
      state.assetIdentifiers.data = {}
      state.assetBytes = {}
    })
    builder.addCase(createDefectAction.rejected, (state) => {
      state.status = 'failed'
    })

    builder.addCase(postDigitalAssetAction.pending, (state) => {
      state.assetIdentifiers.status = 'loading'
    })
    builder.addCase(postDigitalAssetAction.fulfilled, (state, { payload }) => {
      state.assetIdentifiers.data[payload.id] = payload.filename
      if (payload.base64) {
        state.assetBytes[payload.id] = { base64: payload.base64, mimeType: payload.mimeType }
      }
      state.assetIdentifiers.status = 'succeeded'
    })
    builder.addCase(postDigitalAssetAction.rejected, (state, action) => {
      if (action.meta.aborted) {
        state.assetIdentifiers.status = initialState.assetIdentifiers.status
        return
      }
      state.assetIdentifiers.status = 'failed'
    })

    builder.addCase(options.fetchDefectGroupsAction.pending, (state) => {
      state.defectGroups.status = 'loading'
    })
    builder.addCase(options.fetchLimitationsOfUsageAction.pending, (state) => {
      state.limitationsOfUsage.status = 'loading'
    })
    builder.addCase(options.fetchAffectToUsageAction.pending, (state) => {
      state.affectToUsage.status = 'loading'
    })
    builder.addCase(options.fetchDefectTreesAction.pending, (state) => {
      state.defectTrees.status = 'loading'
    })
    builder.addCase(options.fetchItemsAction.pending, (state) => {
      state.items.status = 'loading'
    })
    builder.addCase(options.fetchPositionsActions.pending, (state) => {
      state.positions.status = 'loading'
    })

    // rejected
    builder.addCase(options.fetchDefectGroupsAction.rejected, (state) => {
      state.defectGroups.status = 'failed'
    })
    builder.addCase(options.fetchLimitationsOfUsageAction.rejected, (state) => {
      state.limitationsOfUsage.status = 'failed'
    })
    builder.addCase(options.fetchAffectToUsageAction.rejected, (state) => {
      state.affectToUsage.status = 'failed'
    })
    builder.addCase(options.fetchDefectTreesAction.rejected, (state) => {
      state.defectTrees.status = 'failed'
    })
    builder.addCase(options.fetchItemsAction.rejected, (state) => {
      state.items.status = 'failed'
    })
    builder.addCase(options.fetchPositionsActions.rejected, (state) => {
      state.positions.status = 'failed'
    })

    builder.addMatcher(
      isAnyOf(
        options.fetchDefectGroupsAction.fulfilled,
        options.fetchLimitationsOfUsageAction.fulfilled,
        options.fetchAffectToUsageAction.fulfilled,
        options.fetchDefectTreesAction.fulfilled,
        options.fetchItemsAction.fulfilled,
        options.fetchPositionsActions.fulfilled
      ),
      (state, { payload }) => {
        const { id, data } = payload
        const status = 'succeeded'
        // Would be nice to just use state[id] = data
        // but typescript can't figure out that state[id] and data are the same type
        switch (id) {
          case 'defectGroups':
            state.defectGroups = { data, status }
            break
          case 'items':
            state.items = { data, status }
            break
          case 'defectTrees':
            state.defectTrees = { data, status }
            break
          case 'limitationsOfUsage':
            state.limitationsOfUsage = { data, status }
            break
          case 'affectToUsage':
            state.affectToUsage = { data, status }
            break
          case 'positions':
            state.positions = { data, status }
            break
        }
      }
    )
  },
})

export const equipmentsWithHighlightedSelector = createSelector(
  (state: AppState) => state.defectCommonData.equipments,
  (state: AppState) => state.defect.preSelectedSerialNumbers,
  (equipments, preSelectedSerialNumbers) => ({
    equipments,
    highlightedEquipments: equipments.data.filter((e) =>
      preSelectedSerialNumbers.includes(e.installationGroup)
    ),
  })
)

export const {
  removeFile,
  clearFiles,
  clearDataByKey,
  resetStatus,
  resetOptionsStatusByKey,
  resetStoredResponseRequest,
  setPreSelectedSerialNumbers,
} = createDefectSlice.actions

export default createDefectSlice.reducer
